/**
 * AGC: Airdrop Admin Controls Component
 * AGC: Allows owner to manage airdrop rounds, view stats, and start new rounds
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useState, useEffect } from 'react'
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useReadContract } from 'wagmi'
import { formatEther } from 'viem'
import { Gift, RefreshCw, TrendingUp, Users, Package, AlertCircle, CheckCircle } from 'lucide-react'
import { useModal } from '../../hooks/useModal'

// AGC: Top-level module debug log (after imports)
console.log('🎁🎁🎁 [AirdropAdminControls] MODULE LOADED - imports successful 🎁🎁🎁')

// AGC: Contract addresses from environment (V3 takes priority)
const AIRDROP_MANAGER_V3_ADDRESS = (import.meta.env.VITE_AIRDROP_MANAGER_V3_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const AIRDROP_MANAGER_V2_ADDRESS = (import.meta.env.VITE_AIRDROP_MANAGER_V2_ADDRESS || '0x7AF0F520812d0Ad7bc05217E31B8404a66108143') as `0x${string}`

// AGC: Determine which contract to use (V3 priority)
const AIRDROP_MANAGER_ADDRESS = AIRDROP_MANAGER_V3_ADDRESS !== '0x0000000000000000000000000000000000000000' 
  ? AIRDROP_MANAGER_V3_ADDRESS 
  : AIRDROP_MANAGER_V2_ADDRESS
const IS_V3 = AIRDROP_MANAGER_V3_ADDRESS !== '0x0000000000000000000000000000000000000000'

// AGC: AirdropManager ABI (supports both V2 and V3)
const AIRDROP_MANAGER_ABI = [
  {
    name: 'currentRound',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'startNewRound',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: []
  },
  {
    name: 'getRoundStats',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'round', type: 'uint256' }],
    outputs: [
      { name: 'tokenClaims', type: 'uint256' },
      { name: 'nftClaims', type: 'uint256' },
      { name: 'amountDistributed', type: 'uint256' } // AGC: V3 only (optional)
    ]
  },
  {
    name: 'getAvailableFunds',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  // AGC: V2 function
  {
    name: 'tokenAirdropAmount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  // AGC: V3 functions
  {
    name: 'tokenAirdropAmountFull',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'tokenAirdropAmountHalf',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'lockupDuration',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'buffsPerType',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'potionsPerType',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'paused',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'owner',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }]
  },
  // AGC: V2 function
  {
    name: 'setTokenAirdropAmount',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: '_amount', type: 'uint256' }],
    outputs: []
  },
  // AGC: V3 function
  {
    name: 'setTokenAirdropAmountFull',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: '_amount', type: 'uint256' }],
    outputs: []
  }
] as const

export const AirdropAdminControls: React.FC = () => {
  console.log('🎁🎁🎁 [AirdropAdminControls] COMPONENT RENDER STARTED 🎁🎁🎁')
  
  const { address, isConnected } = useAccount()
  const { alert, confirm } = useModal()
  
  const [currentRound, setCurrentRound] = useState<bigint | null>(null)
  const [roundStats, setRoundStats] = useState<{ tokenClaims: bigint; nftClaims: bigint } | null>(null)
  const [availableFunds, setAvailableFunds] = useState<bigint | null>(null)
  const [tokenAirdropAmount, setTokenAirdropAmount] = useState<bigint | null>(null)
  const [tokenAirdropAmountFull, setTokenAirdropAmountFull] = useState<bigint | null>(null)
  const [tokenAirdropAmountHalf, setTokenAirdropAmountHalf] = useState<bigint | null>(null)
  const [lockupDuration, setLockupDuration] = useState<bigint | null>(null)
  const [buffsPerType, setBuffsPerType] = useState<bigint | null>(null)
  const [potionsPerType, setPotionsPerType] = useState<bigint | null>(null)
  const [isPaused, setIsPaused] = useState<boolean | null>(null)
  const [isOwner, setIsOwner] = useState<boolean>(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [newTokenAmount, setNewTokenAmount] = useState<string>('')
  const [isUpdatingAmount, setIsUpdatingAmount] = useState(false)

  // AGC: Debug logging
  useEffect(() => {
    console.log('🎁 [AirdropAdminControls] Component mounted')
    console.log('🎁 [AirdropAdminControls] Using V3:', IS_V3)
    console.log('🎁 [AirdropAdminControls] Contract address:', AIRDROP_MANAGER_ADDRESS)
    console.log('🎁 [AirdropAdminControls] Wallet connected:', isConnected)
    console.log('🎁 [AirdropAdminControls] Wallet address:', address)
  }, [isConnected, address])

  // AGC: Read current round
  const { data: roundData, refetch: refetchRound, error: roundError, isLoading: isLoadingRound } = useReadContract({
    address: AIRDROP_MANAGER_ADDRESS,
    abi: AIRDROP_MANAGER_ABI,
    functionName: 'currentRound',
    query: {
      enabled: !!AIRDROP_MANAGER_ADDRESS && AIRDROP_MANAGER_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Debug logging for contract reads
  useEffect(() => {
    console.log('🎁 [AirdropAdminControls] Round data:', { roundData, roundError, isLoadingRound, address: AIRDROP_MANAGER_ADDRESS })
  }, [roundData, roundError, isLoadingRound])

  // AGC: Set error state if contract read fails
  useEffect(() => {
    if (roundError) {
      const errorMsg = `Failed to read contract: ${roundError.message || 'Unknown error'}`
      setError(errorMsg)
      console.error('❌ AGC: AirdropAdminControls error:', roundError)
    } else {
      setError(null)
    }
  }, [roundError])

  // AGC: Read owner
  const { data: ownerAddress } = useReadContract({
    address: AIRDROP_MANAGER_ADDRESS,
    abi: AIRDROP_MANAGER_ABI,
    functionName: 'owner',
    query: {
      enabled: !!AIRDROP_MANAGER_ADDRESS && isConnected,
    },
  })

  // AGC: Read contract state
  const { data: pausedState } = useReadContract({
    address: AIRDROP_MANAGER_ADDRESS,
    abi: AIRDROP_MANAGER_ABI,
    functionName: 'paused',
    query: {
      enabled: !!AIRDROP_MANAGER_ADDRESS,
    },
  })

  const { data: fundsData } = useReadContract({
    address: AIRDROP_MANAGER_ADDRESS,
    abi: AIRDROP_MANAGER_ABI,
    functionName: 'getAvailableFunds',
    query: {
      enabled: !!AIRDROP_MANAGER_ADDRESS,
    },
  })

  // AGC: Read token amount (V2 or V3)
  const { data: tokenAmountData, refetch: refetchTokenAmount } = useReadContract({
    address: AIRDROP_MANAGER_ADDRESS,
    abi: AIRDROP_MANAGER_ABI,
    functionName: IS_V3 ? 'tokenAirdropAmountFull' : 'tokenAirdropAmount',
    query: {
      enabled: !!AIRDROP_MANAGER_ADDRESS,
    },
  })

  // AGC: Read half amount (V3 only)
  const { data: tokenAmountHalfData } = useReadContract({
    address: AIRDROP_MANAGER_ADDRESS,
    abi: AIRDROP_MANAGER_ABI,
    functionName: 'tokenAirdropAmountHalf',
    query: {
      enabled: !!AIRDROP_MANAGER_ADDRESS && IS_V3,
    },
  })

  const { data: lockupData } = useReadContract({
    address: AIRDROP_MANAGER_ADDRESS,
    abi: AIRDROP_MANAGER_ABI,
    functionName: 'lockupDuration',
    query: {
      enabled: !!AIRDROP_MANAGER_ADDRESS,
    },
  })

  const { data: buffsData } = useReadContract({
    address: AIRDROP_MANAGER_ADDRESS,
    abi: AIRDROP_MANAGER_ABI,
    functionName: 'buffsPerType',
    query: {
      enabled: !!AIRDROP_MANAGER_ADDRESS,
    },
  })

  const { data: potionsData } = useReadContract({
    address: AIRDROP_MANAGER_ADDRESS,
    abi: AIRDROP_MANAGER_ABI,
    functionName: 'potionsPerType',
    query: {
      enabled: !!AIRDROP_MANAGER_ADDRESS,
    },
  })

  // AGC: Write contract for starting new round
  const { writeContract: startNewRound, isPending: isStartingRound, data: startRoundHash } = useWriteContract()
  
  const { isLoading: isWaitingForRound, isSuccess: roundStarted } = useWaitForTransactionReceipt({
    hash: startRoundHash,
  })

  // AGC: Write contract for updating token amount
  const { writeContractAsync: updateTokenAmount, isPending: isUpdatingAmountPending, error: updateAmountError, data: updateAmountHash } = useWriteContract()
  
  const { isLoading: isWaitingForAmountUpdate, isSuccess: amountUpdated } = useWaitForTransactionReceipt({
    hash: updateAmountHash,
  })

  // AGC: Update state from contract reads
  useEffect(() => {
    if (roundData !== undefined) {
      console.log('🎁 [AirdropAdminControls] Round data received:', roundData)
      setCurrentRound(roundData as bigint)
      setIsInitialized(true)
    }
  }, [roundData])

  // AGC: Set initialized after a short delay to allow data to load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isInitialized) {
        console.warn('⚠️ [AirdropAdminControls] Data loading timeout - showing UI anyway')
        setIsInitialized(true) // AGC: Allow component to render even if data didn't load
      }
    }, 3000) // 3 second timeout
    
    return () => clearTimeout(timer)
  }, [isInitialized])

  useEffect(() => {
    if (ownerAddress && address) {
      const isOwnerCheck = ownerAddress.toLowerCase() === address.toLowerCase()
      setIsOwner(isOwnerCheck)
      console.log('🎁 [AirdropAdminControls] Owner check:', {
        contractOwner: ownerAddress,
        connectedAddress: address,
        isOwner: isOwnerCheck
      })
    } else {
      console.log('🎁 [AirdropAdminControls] Owner check pending:', {
        ownerAddress: ownerAddress || 'loading...',
        connectedAddress: address || 'not connected'
      })
    }
  }, [ownerAddress, address])

  useEffect(() => {
    if (pausedState !== undefined) {
      setIsPaused(pausedState as boolean)
    }
  }, [pausedState])

  useEffect(() => {
    if (fundsData !== undefined) {
      setAvailableFunds(fundsData as bigint)
    }
  }, [fundsData])

  useEffect(() => {
    if (tokenAmountData !== undefined) {
      if (IS_V3) {
        setTokenAirdropAmountFull(tokenAmountData as bigint)
      } else {
        setTokenAirdropAmount(tokenAmountData as bigint)
      }
    }
  }, [tokenAmountData])

  useEffect(() => {
    if (tokenAmountHalfData !== undefined && IS_V3) {
      setTokenAirdropAmountHalf(tokenAmountHalfData as bigint)
    }
  }, [tokenAmountHalfData])

  useEffect(() => {
    if (lockupData !== undefined) {
      setLockupDuration(lockupData as bigint)
    }
  }, [lockupData])

  useEffect(() => {
    if (buffsData !== undefined) {
      setBuffsPerType(buffsData as bigint)
    }
  }, [buffsData])

  useEffect(() => {
    if (potionsData !== undefined) {
      setPotionsPerType(potionsData as bigint)
    }
  }, [potionsData])

  // AGC: Read round stats using wagmi
  const { data: roundStatsData, refetch: refetchRoundStats } = useReadContract({
    address: AIRDROP_MANAGER_ADDRESS,
    abi: AIRDROP_MANAGER_ABI,
    functionName: 'getRoundStats',
    args: currentRound !== null ? [currentRound] : undefined,
    query: {
      enabled: currentRound !== null && !!AIRDROP_MANAGER_ADDRESS,
    },
  })

  // AGC: Update round stats from contract read
  useEffect(() => {
    if (roundStatsData && Array.isArray(roundStatsData) && roundStatsData.length === 2) {
      setRoundStats({
        tokenClaims: roundStatsData[0] as bigint,
        nftClaims: roundStatsData[1] as bigint
      })
    }
  }, [roundStatsData])

  // AGC: Refresh stats when round starts
  useEffect(() => {
    if (roundStarted) {
      setTimeout(() => {
        refetchRound()
        refetchRoundStats()
        alert('✅ New round started! All users can now claim again.')
      }, 2000)
    }
  }, [roundStarted, refetchRound, refetchRoundStats])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await refetchRound()
    await refetchRoundStats()
    setTimeout(() => setIsRefreshing(false), 500)
  }

  const handleStartNewRound = async () => {
    if (!isConnected) {
      await alert('Please connect your wallet first.')
      return
    }

    if (!isOwner) {
      await alert('Only the contract owner can start a new round.')
      return
    }

    const confirmed = await confirm(
      'Start New Airdrop Round?',
      `This will start Round ${currentRound ? Number(currentRound) + 1 : 2}. All users who previously claimed will be able to claim again. This action cannot be undone.`
    )

    if (!confirmed) return

    try {
      startNewRound({
        address: AIRDROP_MANAGER_ADDRESS,
        abi: AIRDROP_MANAGER_ABI,
        functionName: 'startNewRound',
      })
    } catch (error: any) {
      console.error('Failed to start new round:', error)
      await alert(`Failed to start new round: ${error.message || 'Unknown error'}`)
    }
  }

  const handleUpdateTokenAmount = async () => {
    if (!isConnected) {
      await alert('Please connect your wallet first.')
      return
    }

    if (!isOwner) {
      await alert('Only the contract owner can update the token amount.')
      return
    }

    if (!newTokenAmount || isNaN(Number(newTokenAmount)) || Number(newTokenAmount) <= 0) {
      await alert('Please enter a valid amount (must be > 0)')
      return
    }

    const amountInWei = BigInt(Math.floor(Number(newTokenAmount) * 1e18))
    const currentAmount = IS_V3 
      ? (tokenAirdropAmountFull ? formatEther(tokenAirdropAmountFull) : 'N/A')
      : (tokenAirdropAmount ? formatEther(tokenAirdropAmount) : 'N/A')
    
    const confirmed = await confirm(
      `Update ${IS_V3 ? 'Full' : ''} Token Amount to ${newTokenAmount} OGB?`,
      IS_V3
        ? `This will change the full airdrop amount from ${currentAmount} OGB to ${newTokenAmount} OGB. Half amount will automatically be ${Number(newTokenAmount) / 2} OGB.`
        : `This will change the airdrop amount from ${currentAmount} OGB to ${newTokenAmount} OGB for all future claims.`
    )

    if (!confirmed) return

    setIsUpdatingAmount(true)
    console.log('🎁 [AirdropAdminControls] Attempting to update token amount:', {
      newAmount: newTokenAmount,
      amountInWei: amountInWei.toString(),
      contractAddress: AIRDROP_MANAGER_ADDRESS,
      isV3: IS_V3
    })
    
    try {
      const hash = await updateTokenAmount({
        address: AIRDROP_MANAGER_ADDRESS,
        abi: AIRDROP_MANAGER_ABI,
        functionName: IS_V3 ? 'setTokenAirdropAmountFull' : 'setTokenAirdropAmount',
        args: [amountInWei],
      })
      console.log('✅ [AirdropAdminControls] Transaction submitted:', hash)
      // AGC: Don't set isUpdatingAmount to false here - wait for transaction receipt
    } catch (error: any) {
      console.error('❌ [AirdropAdminControls] Failed to update token amount:', error)
      setIsUpdatingAmount(false)
      await alert(`Failed to update token amount: ${error.message || error.shortMessage || 'Unknown error'}\n\nDetails: ${JSON.stringify(error, null, 2)}`)
    }
  }

  // AGC: Show error if transaction fails
  useEffect(() => {
    if (updateAmountError) {
      console.error('❌ [AirdropAdminControls] Update amount error:', updateAmountError)
      setIsUpdatingAmount(false)
      alert(`Transaction error: ${updateAmountError.message || updateAmountError.shortMessage || 'Unknown error'}`)
    }
  }, [updateAmountError])

  // AGC: Reset form when amount is updated
  useEffect(() => {
    if (amountUpdated) {
      setNewTokenAmount('')
      setIsUpdatingAmount(false)
      // AGC: Refetch token amount to show updated value
      setTimeout(async () => {
        await refetchTokenAmount()
        await alert('✅ Token amount updated successfully!')
      }, 2000)
    }
  }, [amountUpdated, refetchTokenAmount])

  const formatDuration = (seconds: bigint) => {
    const days = Number(seconds) / 86400
    if (days >= 1) {
      return `${days.toFixed(0)} days`
    }
    const hours = Number(seconds) / 3600
    if (hours >= 1) {
      return `${hours.toFixed(0)} hours`
    }
    const minutes = Number(seconds) / 60
    return `${minutes.toFixed(0)} minutes`
  }

  // AGC: Show error if contract address is invalid
  if (!AIRDROP_MANAGER_ADDRESS || AIRDROP_MANAGER_ADDRESS === '0x0000000000000000000000000000000000000000') {
    console.error('❌ [AirdropAdminControls] Invalid contract address:', AIRDROP_MANAGER_ADDRESS)
    return (
      <div className="bg-red-900/40 border border-red-500/40 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-300 mb-2">Configuration Error</h3>
        <p className="text-red-200">
          AirdropManager address is not configured. Please set VITE_AIRDROP_MANAGER_V3_ADDRESS or VITE_AIRDROP_MANAGER_V2_ADDRESS in env.public
        </p>
        <p className="text-xs text-red-300 mt-2">
          Current value: {AIRDROP_MANAGER_ADDRESS || 'undefined'}
        </p>
      </div>
    )
  }

  // AGC: Always render something - even if data is missing
  console.log('🎁 [AirdropAdminControls] Rendering component with state:', {
    isInitialized,
    currentRound: currentRound?.toString(),
    error,
    roundError: roundError?.message,
    isLoadingRound
  })

  return (
    <div className="space-y-6">
      {/* AGC: Debug info (remove in production) */}
      <div className="bg-slate-900/50 rounded-lg p-2 text-xs text-gray-500 font-mono">
        <div>Debug Info:</div>
        <div>• Initialized: {String(isInitialized)}</div>
        <div>• Current Round: {currentRound?.toString() || 'null'}</div>
        <div>• Error: {error || 'none'}</div>
        <div>• Round Error: {roundError?.message || 'none'}</div>
        <div>• Loading: {String(isLoadingRound)}</div>
        <div>• Contract: {AIRDROP_MANAGER_ADDRESS}</div>
        <div>• Version: {IS_V3 ? 'V3 (Variable Amounts)' : 'V2 (Fixed Amount)'}</div>
        <div>• Connected: {String(isConnected)}</div>
      </div>

      {/* AGC: Error Display */}
      {error && (
        <div className="bg-red-900/40 border border-red-500/40 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-300" />
            <p className="text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* AGC: Header */}
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Gift className="w-6 h-6 text-purple-400" />
            <h3 className="text-xl font-bold text-white">Airdrop Management</h3>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors flex items-center gap-2 ${
              isRefreshing
                ? 'bg-slate-700 text-gray-400 border-slate-600 cursor-not-allowed'
                : 'bg-slate-700 hover:bg-slate-600 text-white border-slate-500'
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* AGC: Current Round Display */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-gray-400">Current Round</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {currentRound !== null ? `Round ${currentRound.toString()}` : isLoadingRound ? 'Loading...' : 'N/A'}
            </p>
            {isLoadingRound && (
              <p className="text-xs text-gray-500 mt-1">Reading from contract...</p>
            )}
          </div>

          <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-green-400" />
              <span className="text-sm text-gray-400">Token Claims</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {roundStats ? roundStats.tokenClaims.toString() : currentRound ? '0' : 'N/A'}
            </p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-gray-400">NFT Claims</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {roundStats ? roundStats.nftClaims.toString() : currentRound ? '0' : 'N/A'}
            </p>
          </div>
        </div>

        {/* AGC: Status Indicators */}
        <div className="flex gap-4">
          {isPaused !== null && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
              isPaused 
                ? 'bg-red-900/40 text-red-300 border border-red-500/40' 
                : 'bg-green-900/40 text-green-300 border border-green-500/40'
            }`}>
              {isPaused ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
              <span className="text-sm font-medium">
                {isPaused ? 'Paused' : 'Active'}
              </span>
            </div>
          )}
          {availableFunds !== null && (tokenAirdropAmount !== null || tokenAirdropAmountFull !== null) && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-900/40 text-blue-300 border border-blue-500/40">
              <span className="text-sm font-medium">
                Funds: {formatEther(availableFunds)} OGB
              </span>
              <span className="text-xs text-gray-400">
                (~{availableFunds && (tokenAirdropAmountFull || tokenAirdropAmount) 
                  ? (Number(availableFunds) / Number(tokenAirdropAmountFull || tokenAirdropAmount)).toFixed(0) 
                  : 0} {IS_V3 ? 'full' : ''} claims)
              </span>
            </div>
          )}
        </div>
      </div>

      {/* AGC: Airdrop Configuration */}
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h4 className="text-lg font-semibold text-white mb-4">Airdrop Configuration</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
            <span className="text-sm text-gray-400">
              {IS_V3 ? 'Full Amount per Claim' : 'Token Amount per Claim'}
            </span>
            <p className="text-xl font-bold text-white mt-1">
              {IS_V3 
                ? (tokenAirdropAmountFull !== null ? formatEther(tokenAirdropAmountFull) : 'Loading...')
                : (tokenAirdropAmount !== null ? formatEther(tokenAirdropAmount) : 'Loading...')
              } OGB
            </p>
            
            {/* AGC: V3 - Show half amount */}
            {IS_V3 && tokenAirdropAmountHalf !== null && (
              <p className="text-sm text-gray-400 mt-1">
                Half Amount: {formatEther(tokenAirdropAmountHalf)} OGB (for suspicious users)
              </p>
            )}
            
            {/* AGC: Always show update UI, but disable if not owner */}
            <div className="mt-3">
              <div className="flex gap-2">
                <input
                  type="number"
                  value={newTokenAmount}
                  onChange={(e) => setNewTokenAmount(e.target.value)}
                  placeholder={`Enter new ${IS_V3 ? 'full' : ''} amount (e.g., 2000)`}
                  className="flex-1 px-3 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                  min="0"
                  step="0.01"
                  disabled={!isConnected || !isOwner || isUpdatingAmountPending || isWaitingForAmountUpdate}
                />
                <button
                  onClick={handleUpdateTokenAmount}
                  disabled={!isConnected || !isOwner || !newTokenAmount || isUpdatingAmountPending || isWaitingForAmountUpdate || isNaN(Number(newTokenAmount)) || Number(newTokenAmount) <= 0}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    !isConnected || !isOwner || !newTokenAmount || isUpdatingAmountPending || isWaitingForAmountUpdate || isNaN(Number(newTokenAmount)) || Number(newTokenAmount) <= 0
                      ? 'bg-slate-700 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {isUpdatingAmountPending || isWaitingForAmountUpdate ? 'Updating...' : 'Update'}
                </button>
              </div>
              {!isConnected && (
                <p className="text-xs text-yellow-400 mt-2">⚠️ Please connect your wallet</p>
              )}
              {isConnected && !isOwner && (
                <p className="text-xs text-red-400 mt-2">⚠️ Only contract owner can update amount. Your address: {address?.slice(0, 6)}...{address?.slice(-4)}</p>
              )}
              {isConnected && isOwner && !newTokenAmount && (
                <p className="text-xs text-gray-400 mt-2">💡 Enter a new amount above to update (e.g., {IS_V3 ? '2000' : '1000'} for {IS_V3 ? '2000' : '1000'} OGB)</p>
              )}
            </div>
            
            {IS_V3 ? (
              <div className="text-xs text-gray-400 mt-2 space-y-1">
                <p>✅ <strong>V3 Variable Amounts:</strong></p>
                <p>• Score &lt; 30: Full amount ({tokenAirdropAmountFull ? formatEther(tokenAirdropAmountFull) : 'N/A'} OGB)</p>
                <p>• Score 30-59: Half amount ({tokenAirdropAmountHalf ? formatEther(tokenAirdropAmountHalf) : 'N/A'} OGB)</p>
                <p>• Score ≥ 60: 0 OGB (rejected)</p>
              </div>
            ) : (
              <p className="text-xs text-gray-500 mt-2">
                ⚠️ Note: V2 uses fixed amount. Upgrade to V3 for variable amounts based on risk score.
              </p>
            )}
          </div>
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
            <span className="text-sm text-gray-400">Lockup Duration</span>
            <p className="text-xl font-bold text-white mt-1">
              {lockupDuration !== null ? formatDuration(lockupDuration) : 'Loading...'}
            </p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
            <span className="text-sm text-gray-400">Buffs per Type</span>
            <p className="text-xl font-bold text-white mt-1">
              {buffsPerType !== null ? buffsPerType.toString() : 'Loading...'}
            </p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
            <span className="text-sm text-gray-400">Potions per Type</span>
            <p className="text-xl font-bold text-white mt-1">
              {potionsPerType !== null ? potionsPerType.toString() : 'Loading...'}
            </p>
          </div>
        </div>
      </div>

      {/* AGC: Start New Round */}
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h4 className="text-lg font-semibold text-white mb-4">Round Management</h4>
        
        <div className="bg-blue-900/20 border border-blue-500/40 rounded-lg p-4 mb-4">
          <h5 className="font-semibold text-blue-300 mb-2 flex items-center gap-2">
            <Gift className="w-5 h-5" />
            How Airdrop Rounds Work
          </h5>
          <ul className="text-sm text-gray-300 space-y-2 list-disc list-inside">
            <li>Each round allows users to claim tokens and NFTs once</li>
            <li>Starting a new round resets all claim statuses - everyone can claim again!</li>
            <li>Round statistics are tracked separately for each round</li>
            <li>Tokens are staked directly to StakingManagerV5 with a {lockupDuration ? formatDuration(lockupDuration) : '60-day'} lockup</li>
            <li>NFTs are minted separately: {buffsPerType ? buffsPerType.toString() : '5'} buffs of each type + {potionsPerType ? potionsPerType.toString() : '1'} potion of each type (COMMON)</li>
            <li>Users can pay battles from their staked airdrop tokens, but cannot unstake until lockup expires</li>
          </ul>
        </div>

        <button
          onClick={handleStartNewRound}
          disabled={!isConnected || !isOwner || isStartingRound || isWaitingForRound}
          className={`w-full px-4 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
            !isConnected || !isOwner || isStartingRound || isWaitingForRound
              ? 'bg-slate-700 text-gray-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 text-white'
          }`}
        >
          {isStartingRound || isWaitingForRound ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              {isStartingRound ? 'Starting Round...' : 'Waiting for confirmation...'}
            </>
          ) : (
            <>
              <Gift className="w-5 h-5" />
              Start Round {currentRound ? Number(currentRound) + 1 : 2}
            </>
          )}
        </button>

        {!isConnected && (
          <p className="text-sm text-yellow-400 mt-2 text-center">
            ⚠️ Please connect your wallet to start a new round
          </p>
        )}

        {isConnected && !isOwner && (
          <p className="text-sm text-red-400 mt-2 text-center">
            ⚠️ Only the contract owner can start new rounds
          </p>
        )}
      </div>
    </div>
  )
}

